/**
 * useConditionInsights
 *
 * Reads patient conditions from medical history and
 * provides step-relevant condition content for the current journey.
 */

import { useMemo } from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import type { ConditionKey, PlanStep } from "../plan.types";
import {
  getConditionContentForStep,
  type ConditionStepContent,
} from "./conditionContent";

export type ConditionInsight = {
  stepId: string;
  content: ConditionStepContent[];
};

type PatientConditionSource = {
  hasEndocrineDiseases?: boolean | null;
  endocrineDiseases?: string[] | null;
  hasReproductiveConditions?: boolean | null;
  reproductiveConditions?: string[] | null;
  endometriosis?: boolean | null;
  hasEndometriosis?: boolean | null;
  maleFactorInfertility?: boolean | null;
  hasMaleFactor?: boolean | null;
  pregnancyLosses?: number | null;
  recurrentPregnancyLoss?: boolean | null;
  diminishedOvarianReserve?: boolean | null;
  hasDiminishedReserve?: boolean | null;
  dateOfBirth?: string | null;
};

/**
 * Extract condition keys from patient medical history
 */
function extractConditions(
  patient: PatientConditionSource | null | undefined,
): ConditionKey[] {
  const conditions: ConditionKey[] = [];
  if (!patient) return conditions;

  // Check endocrine conditions (PMOS (ex PCOS), thyroid)
  if (patient.hasEndocrineDiseases) {
    const diseases = patient.endocrineDiseases as string[] | undefined;
    if (diseases?.includes("pcos")) conditions.push("pcos");
    if (
      diseases?.includes("hypothyroidism") ||
      diseases?.includes("hyperthyroidism") ||
      diseases?.includes("hashimotos")
    ) {
      conditions.push("thyroid");
    }
  }

  // Check for endometriosis (reproductive conditions)
  if (patient.hasReproductiveConditions) {
    const repro = patient.reproductiveConditions as string[] | undefined;
    if (repro?.includes("endometriosis")) conditions.push("endometriosis");
  }
  // Also check gynecological history
  if (patient.endometriosis === true || patient.hasEndometriosis === true) {
    if (!conditions.includes("endometriosis")) {
      conditions.push("endometriosis");
    }
  }

  // Check for male factor (semen analysis results or male infertility diagnosis)
  if (
    patient.maleFactorInfertility === true ||
    patient.hasMaleFactor === true
  ) {
    conditions.push("male-factor");
  }

  // Check for recurrent loss
  const losses = patient.pregnancyLosses as number | undefined;
  if (losses !== undefined && losses >= 2) {
    conditions.push("recurrent-loss");
  }
  if (patient.recurrentPregnancyLoss === true) {
    conditions.push("recurrent-loss");
  }

  // Check for diminished ovarian reserve
  if (
    patient.diminishedOvarianReserve === true ||
    patient.hasDiminishedReserve === true
  ) {
    conditions.push("diminished-reserve");
  }

  // Age 38+
  if (patient.dateOfBirth) {
    const birthDate = new Date(patient.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    if (age >= 38) conditions.push("age-38-plus");
  }

  return conditions;
}

/**
 * Hook: provides condition-specific insights for each step in the journey
 */
export function useConditionInsights(steps: PlanStep[]): {
  conditions: ConditionKey[];
  insights: ConditionInsight[];
  hasConditions: boolean;
} {
  const { data: patient } = usePatient();

  const conditions = useMemo(() => extractConditions(patient), [patient]);

  const insights = useMemo(() => {
    if (conditions.length === 0) return [];

    const result: ConditionInsight[] = [];
    for (const step of steps) {
      const content = getConditionContentForStep(conditions, step.id);
      if (content.length > 0) {
        result.push({ stepId: step.id, content });
      }
    }
    return result;
  }, [conditions, steps]);

  return {
    conditions,
    insights,
    hasConditions: conditions.length > 0,
  };
}
